import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../hooks/useAuth';
import { Home, Dumbbell, Users, Calendar, Menu, X, LogOut, User, Settings, FileText, Shield, Copy, Package } from 'lucide-react';

interface NavigationProps {
  currentPage?: 'dashboard' | 'exercises' | 'clients' | 'workouts' | 'templates' | 'packages' | 'settings' | 'admin';
}

const Navigation: React.FC<NavigationProps> = ({ currentPage }) => {
  const { trainer, logout } = useAuth();
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [isDesktopMenuOpen, setIsDesktopMenuOpen] = useState(false);

  // Закрытие меню при клике вне его
  React.useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as Element;
      if ((isMobileMenuOpen || isDesktopMenuOpen) && !target.closest('nav')) {
        setIsMobileMenuOpen(false);
        setIsDesktopMenuOpen(false);
      }
    };

    if (isMobileMenuOpen || isDesktopMenuOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isMobileMenuOpen, isDesktopMenuOpen]);

  const getLinkClass = (page: string) => {
    const baseClass = "px-3 py-2 rounded-md text-sm font-medium";
    return currentPage === page 
      ? `${baseClass} text-blue-600 hover:text-blue-700`
      : `${baseClass} text-gray-700 hover:text-gray-900`;
  };

  const getMobileLinkClass = (page: string) => {
    const baseClass = "flex flex-col items-center justify-center px-1 py-2 rounded-md text-xs font-medium transition-colors duration-200 min-w-[50px]";
    return currentPage === page 
      ? `${baseClass} text-blue-600 bg-blue-50`
      : `${baseClass} text-gray-600 hover:text-gray-900 hover:bg-gray-50`;
  };

  return (
    <nav className="bg-white shadow relative">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex justify-between h-16">
          <div className="flex items-center">
            {/* Десктопное меню - скрыто на мобильных */}
            <div className="hidden md:block">
              <nav className="flex space-x-4">
                <Link
                  to="/dashboard"
                  className={getLinkClass('dashboard')}
                >
                  Дашборд
                </Link>
                <Link
                  to="/exercises"
                  className={getLinkClass('exercises')}
                >
                  Упражнения
                </Link>
                <Link
                  to="/clients"
                  className={getLinkClass('clients')}
                >
                  Клиенты
                </Link>
                <Link
                  to="/workouts"
                  className={getLinkClass('workouts')}
                >
                  Тренировки
                </Link>
              </nav>
            </div>
          </div>
          
          <div className="flex items-center">
            {/* Информация о пользователе - скрыта на мобильных */}
            <span className="hidden sm:block text-gray-700 mr-4">
              Добро пожаловать, {trainer?.first_name} {trainer?.last_name}!
            </span>
            
            {/* Десктопное гамбургер-меню */}
            <div className="hidden sm:block relative">
              <button
                onClick={() => setIsDesktopMenuOpen(!isDesktopMenuOpen)}
                className="flex items-center justify-center p-2 rounded-md text-gray-600 hover:text-gray-900 hover:bg-gray-100 transition-colors duration-200"
                title="Меню"
              >
                {isDesktopMenuOpen ? (
                  <X size={20} />
                ) : (
                  <Menu size={20} />
                )}
              </button>
              
              {/* Выпадающее десктопное меню */}
              {isDesktopMenuOpen && (
                <div className="absolute right-0 top-full mt-1 w-48 bg-white rounded-md shadow-lg py-1 z-50 border border-gray-200 transform transition-all duration-200 ease-out opacity-100 scale-100">
                  <div className="px-4 py-2 text-xs text-gray-500 border-b border-gray-100">
                    {trainer?.first_name} {trainer?.last_name}
                  </div>
                  {/* Место для будущих пунктов меню */}
                  <Link
                    to="/templates"
                    onClick={() => setIsDesktopMenuOpen(false)}
                    className="w-full flex items-center px-4 py-2 text-sm text-gray-600 hover:bg-gray-50 transition-colors duration-200"
                  >
                    <Copy size={16} className="mr-3" />
                    Шаблоны
                  </Link>
                  <Link
                    to="/packages"
                    onClick={() => setIsDesktopMenuOpen(false)}
                    className="w-full flex items-center px-4 py-2 text-sm text-gray-600 hover:bg-gray-50 transition-colors duration-200"
                  >
                    <Package size={16} className="mr-3" />
                    Пакеты
                  </Link>
                  <button
                    className="w-full flex items-center px-4 py-2 text-sm text-gray-600 hover:bg-gray-50 transition-colors duration-200 opacity-50 cursor-not-allowed"
                    disabled
                  >
                    <User size={16} className="mr-3" />
                    Профиль (скоро)
                  </button>
                  <Link
                    to="/settings"
                    onClick={() => setIsDesktopMenuOpen(false)}
                    className="w-full flex items-center px-4 py-2 text-sm text-gray-600 hover:bg-gray-50 transition-colors duration-200"
                  >
                    <Settings size={16} className="mr-3" />
                    Настройки
                  </Link>
                  {trainer?.is_admin && (
                    <>
                      <Link
                        to="/logs"
                        onClick={() => setIsDesktopMenuOpen(false)}
                        className="w-full flex items-center px-4 py-2 text-sm text-gray-600 hover:bg-gray-50 transition-colors duration-200"
                      >
                        <FileText size={16} className="mr-3" />
                        Логи
                      </Link>
                      <Link
                        to="/admin"
                        onClick={() => setIsDesktopMenuOpen(false)}
                        className="w-full flex items-center px-4 py-2 text-sm text-gray-600 hover:bg-gray-50 transition-colors duration-200"
                      >
                        <Shield size={16} className="mr-3" />
                        Админка
                      </Link>
                    </>
                  )}
                  <div className="border-t border-gray-100 my-1"></div>
                  <button
                    onClick={() => {
                      setIsDesktopMenuOpen(false);
                      logout();
                    }}
                    className="w-full flex items-center px-4 py-2 text-sm text-red-600 hover:bg-red-50 transition-colors duration-200"
                  >
                    <LogOut size={16} className="mr-3" />
                    Выйти
                  </button>
                </div>
              )}
            </div>
            

            
            {/* Мобильное меню с иконками - смещено влево */}
            <div className="md:hidden flex items-center space-x-1 -ml-4">
              <Link
                to="/dashboard"
                className={getMobileLinkClass('dashboard')}
                title="Дашборд"
              >
                <Home size={20} className="sm:w-5 sm:h-5" />
                <span className="mt-1 text-xs">Главная</span>
              </Link>
              <Link
                to="/exercises"
                className={getMobileLinkClass('exercises')}
                title="Упражнения"
              >
                <Dumbbell size={20} className="sm:w-5 sm:h-5" />
                <span className="mt-1 text-xs">Упражнения</span>
              </Link>
              <Link
                to="/clients"
                className={getMobileLinkClass('clients')}
                title="Клиенты"
              >
                <Users size={20} className="sm:w-5 sm:h-5" />
                <span className="mt-1 text-xs">Клиенты</span>
              </Link>
              <Link
                to="/workouts"
                className={getMobileLinkClass('workouts')}
                title="Тренировки"
              >
                <Calendar size={20} className="sm:w-5 sm:h-5" />
                <span className="mt-1 text-xs">Тренировки</span>
              </Link>
              
              {/* Гамбургер-меню для дополнительных опций */}
              <div className="relative">
                <button
                  onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
                  className="flex flex-col items-center justify-center px-1 py-2 rounded-md text-xs font-medium text-gray-600 hover:text-gray-900 hover:bg-gray-50 transition-colors duration-200 min-w-[40px]"
                  title="Меню"
                >
                  {isMobileMenuOpen ? (
                    <X size={20} className="sm:w-5 sm:h-5" />
                  ) : (
                    <Menu size={20} className="sm:w-5 sm:h-5" />
                  )}
                </button>
                
                {/* Выпадающее меню */}
                {isMobileMenuOpen && (
                  <div className="absolute right-0 top-full mt-1 w-48 bg-white rounded-md shadow-lg py-1 z-50 border border-gray-200 transform transition-all duration-200 ease-out opacity-100 scale-100">
                    <div className="px-4 py-2 text-xs text-gray-500 border-b border-gray-100">
                      {trainer?.first_name} {trainer?.last_name}
                    </div>
                    {/* Место для будущих пунктов меню */}
                    <Link
                      to="/templates"
                      onClick={() => setIsMobileMenuOpen(false)}
                      className="w-full flex items-center px-4 py-2 text-sm text-gray-600 hover:bg-gray-50 transition-colors duration-200"
                    >
                      <Copy size={16} className="mr-3" />
                      Шаблоны
                    </Link>
                    <Link
                      to="/packages"
                      onClick={() => setIsMobileMenuOpen(false)}
                      className="w-full flex items-center px-4 py-2 text-sm text-gray-600 hover:bg-gray-50 transition-colors duration-200"
                    >
                      <Package size={16} className="mr-3" />
                      Пакеты
                    </Link>
                    <button
                      className="w-full flex items-center px-4 py-2 text-sm text-gray-600 hover:bg-gray-50 transition-colors duration-200 opacity-50 cursor-not-allowed"
                      disabled
                    >
                      <User size={16} className="mr-3" />
                      Профиль (скоро)
                    </button>
                    <Link
                      to="/settings"
                      onClick={() => setIsMobileMenuOpen(false)}
                      className="w-full flex items-center px-4 py-2 text-sm text-gray-600 hover:bg-gray-50 transition-colors duration-200"
                    >
                      <Settings size={16} className="mr-3" />
                      Настройки
                    </Link>
                    {trainer?.is_admin && (
                      <>
                        <Link
                          to="/logs"
                          onClick={() => setIsMobileMenuOpen(false)}
                          className="w-full flex items-center px-4 py-2 text-sm text-gray-600 hover:bg-gray-50 transition-colors duration-200"
                        >
                          <FileText size={16} className="mr-3" />
                          Логи
                        </Link>
                        <Link
                          to="/admin"
                          onClick={() => setIsMobileMenuOpen(false)}
                          className="w-full flex items-center px-4 py-2 text-sm text-gray-600 hover:bg-gray-50 transition-colors duration-200"
                        >
                          <Shield size={16} className="mr-3" />
                          Админка
                        </Link>
                      </>
                    )}
                    <div className="border-t border-gray-100 my-1"></div>
                    <button
                      onClick={() => {
                        setIsMobileMenuOpen(false);
                        logout();
                      }}
                      className="w-full flex items-center px-4 py-2 text-sm text-red-600 hover:bg-red-50 transition-colors duration-200"
                    >
                      <LogOut size={16} className="mr-3" />
                      Выйти
                    </button>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
    </nav>
  );
};

export default Navigation; 